import { useState, useEffect } from "react";

import Wrapper from "../utils/wrapper";
import FilterButton from "../utils/filterButton";
import Menu from "../utils/menu";

import {
  Title,
  Card,
  Modal,
  Button,
  InputText,
  Dropdown,
  Tooltip,
  InputTextArea,
  IconButton,
  BadgeItem,
} from "@components";
import {
  Trash,
  ExternalLink,
  LayoutGrid,
  AlignLeft,
  Star,
  EllipsisVertical,
  Plus,
  Store,
  ArrowRight,
  Presentation,
  X,
  MicVocal,
  FolderArchive,
  Map,
  Building2,
} from "lucide-react";

import {
  EnseignesActions,
  CandidatsActions,
  BailleursActions,
  VillesActions,
} from "../profileActions";

const ACTIONS_PER_CURRENT_USER_TYPE = {
  ccurrent_user_type_enseigne: EnseignesActions,
  ccurrent_user_type_candidat: CandidatsActions,
  ccurrent_user_type_bailleur: BailleursActions,
  ccurrent_user_type_ville: VillesActions,
};

const MESSAGES = {
  presentation: {
    object: "Demande de présentation de votre franchise",
    message: `Madame, Monsieur,

Je m'intéresse à votre concept et souhaiterais obtenir une présentation détaillée de votre franchise.

Pourriez-vous me faire parvenir une documentation complète sur :
- Votre concept et ses avantages
- Les conditions d'accès à la franchise
- Le montant des investissements nécessaires
- Les perspectives de développement

Je vous remercie par avance de votre retour.

Cordialement,
[Votre nom]`,
  },

  pdv: {
    object: "Demande de visite d'un point de vente",
    message: `Madame, Monsieur,

Je souhaiterais visiter l'un de vos points de vente afin de mieux comprendre votre concept et son fonctionnement au quotidien.

Serait-il possible d'organiser une visite dans les prochaines semaines ?

Je suis disponible [préciser vos disponibilités].

Dans l'attente de votre retour,
Cordialement,
[Votre nom]`,
  },

  talk: {
    object: "Demande de mise en relation avec un franchisé",
    message: `Madame, Monsieur,

Dans le cadre de mon projet de franchise, j'aimerais échanger avec l'un de vos franchisés pour bénéficier de son retour d'expérience.

Pourriez-vous me mettre en relation avec l'un d'entre eux ?

Je vous en remercie par avance.

Cordialement,
[Votre nom]`,
  },

  submit: {
    object: "Proposition de projet franchise",
    message: `Madame, Monsieur,

Je souhaite vous soumettre mon projet d'ouverture d'une franchise dans la ville de [ville].

Vous trouverez en pièce jointe mon dossier de candidature comprenant :
- Mon CV
- Une présentation détaillée du projet
- Un prévisionnel financier
- Une étude de la zone d'implantation

Je reste à votre disposition pour échanger plus en détail sur ce projet.

Cordialement,
[Votre nom]`,
  },

  pdvlist: {
    object: "Demande de liste des points de vente à reprendre",
    message: `Madame, Monsieur,

Je suis intéressé par la reprise d'un point de vente existant.

Pourriez-vous me communiquer la liste des établissements actuellement disponibles à la reprise, ainsi que leurs conditions ?

Je vous remercie par avance,
Cordialement,
[Votre nom]`,
  },

  citylist: {
    object: "Demande de liste des villes prioritaires",
    message: `Madame, Monsieur,

Dans le cadre de mon projet de franchise, je souhaiterais connaître vos zones de développement prioritaires.

Pourriez-vous me transmettre la liste des villes où vous souhaitez vous implanter en priorité ?

Je vous remercie par avance de votre retour.

Cordialement,
[Votre nom]`,
  },
};

export function EnseignesSearchBar({
  children,
  currentUser,
  selectedItems,
  onDeselectAll,
}) {
  const [currentMessageTemplate, setCurrentMessageTemplate] = useState(null);
  const [editableMessage, setEditableMessage] = useState("");

  useEffect(() => {
    setEditableMessage(MESSAGES[currentMessageTemplate]?.message);
  }, [currentMessageTemplate]);

  const ProfileActions =
    ACTIONS_PER_CURRENT_USER_TYPE[
      `current_user_type_${currentUser.profileType}`
    ];

  const params = new URLSearchParams(window.location.search);
  const display = params.get("display");

  return (
    <>
      <Wrapper>
        <Dropdown button={<FilterButton active label="Filtrer" />}>
          <Menu
            items={[
              { selected: true, label: "Les derniers concepts", href: "" },
              { label: "Concepts les plus développés", href: "" },
              { label: "Concepts les moins chers", href: "" },
              { label: "Concepts sans local", href: "" },
              { label: "Franchises fermées", href: "" },
            ]}
          />
        </Dropdown>

        <Dropdown button={<FilterButton label="Apport" />}>
          <div className="flex flex-col gap-2 p-4">
            <InputText label="Apport minimum" name="apportMin" type="number" />
            <InputText label="Apport maximum" name="apportMax" type="number" />
          </div>
        </Dropdown>

        <Dropdown button={<FilterButton label="Surface" />}>
          <div className="flex flex-col gap-2 p-4">
            <InputText
              label="Surface minimum"
              name="surfaceMin"
              type="number"
            />
            <InputText
              label="Surface maximum"
              name="surfaceMax"
              type="number"
            />
          </div>
        </Dropdown>

        <Dropdown button={<FilterButton label="Chalandise" />}>
          <div className="flex flex-col gap-2 p-4">
            <InputText
              label="Chalandise minimum"
              name="chalandiseMin"
              type="number"
            />
            <InputText
              label="Chalandise maximum"
              name="chalandiseMax"
              type="number"
            />
          </div>
        </Dropdown>

        <div className="h-[36px] border-l border-slate-200 w-[1px] mx-2"></div>

        <Tooltip content="Filter vos favoris">
          <FilterButton dropdown={false} label={<Star className="w-4 h-4" />} />
        </Tooltip>

        <div className="h-[36px] border-l border-slate-200 w-[1px] mx-2"></div>

        <Tooltip content="Afficher en grille">
          <FilterButton
            active={!display || display === "grid"}
            dropdown={false}
            href="/search/enseignes?display=grid"
            label={<LayoutGrid className="w-4 h-4" />}
          />
        </Tooltip>
        <Tooltip content="Afficher en liste">
          <FilterButton
            active={display === "list"}
            dropdown={false}
            href="/search/enseignes?display=list"
            label={<AlignLeft className="w-4 h-4" />}
          />
        </Tooltip>
      </Wrapper>

      {selectedItems.length > 0 && (
        <div className="z-[20] fixed bottom-4 left-0 right-0 flex items-center justify-center">
          <div className="rounded-lg shadow ring-1 ring-black/5 bg-white py-3 px-4 flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="w-6 h-6 flex items-center justify-center bg-default-50 text-default-700 rounded ring-1 ring-default-200">
                {selectedItems.length}
              </span>
              <span className="inline-flex pr-1">
                enseigne
                {selectedItems.length > 1 && "s"} sélectionnée
                {selectedItems.length > 1 && "s"}
              </span>
              <button
                onClick={onDeselectAll}
                className="w-8 h-8 rounded-full flex items-center justify-center bg-default-100"
              >
                <Trash className="w-4 h-4 text-default-400" />
              </button>
            </div>
            <div className="inline-flex space-x-2">
              <div className="grid grid-cols-3 [&>button]:justify-center gap-2">
                <Button
                  size="small"
                  label="Ajouter aux favorie"
                  icon={Star}
                  onClick={() => {}}
                />
                <Button
                  size="small"
                  label="Demander une présentation"
                  icon={Presentation}
                  onClick={() => setCurrentMessageTemplate("presentation")}
                />
                <Button
                  size="small"
                  label="Visiter un point de vente"
                  icon={Store}
                  onClick={() => setCurrentMessageTemplate("pdv")}
                />
              </div>
              <div>
                <Dropdown button={<Button size="small" icon={Plus} />}>
                  <Menu
                    items={[
                      {
                        label: "Parler à un franchisé",
                        onClick: () => setCurrentMessageTemplate("talk"),
                        icon: MicVocal,
                      },
                      {
                        label: "Proposer un projet",
                        onClick: () => setCurrentMessageTemplate("submit"),
                        icon: FolderArchive,
                      },
                      {
                        label:
                          "Recevoir la liste des point de vente à reprendre",
                        onClick: () => setCurrentMessageTemplate("pdvlist"),
                        icon: Map,
                      },
                      {
                        label: "Recevoir la liste des villes prioritaires",
                        onClick: () => setCurrentMessageTemplate("citylist"),
                        icon: Building2,
                      },
                    ]}
                  />
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADDS FOOTER ACTIONS WHEN ITEMS ARE SELECTED */}
      <Modal
        isOpen={!!currentMessageTemplate}
        onClose={() => setCurrentMessageTemplate(null)}
      >
        <Card className="w-[600px]">
          <div className="flex items-center justify-between px-6 py-3 border-b">
            <Title>{MESSAGES[currentMessageTemplate]?.object}</Title>
            <div>
              <IconButton
                icon={X}
                onClick={() => setCurrentMessageTemplate(null)}
              />
            </div>
          </div>
          <div className="px-6 pt-3 space-x-2">
            <BadgeItem
              label="Amorino"
              src="https://s3.eu-west-3.amazonaws.com/insiti.com/logos/891/big_square/amorino.jpg"
            />
            <BadgeItem
              label="ADA"
              src="https://s3.eu-west-3.amazonaws.com/insiti.com/logos/3725/big_square/franchise_ada_ouvrir_ou_reprendre.jpeg"
            />
            <BadgeItem
              label="Akena"
              src="https://s3.eu-west-3.amazonaws.com/insiti.com/logos/889/big_square/LogoAkena_3etoiles-Posi-RVB.png"
            />
          </div>
          <div className="p-6 py-3">
            <InputTextArea
              name="message"
              value={editableMessage}
              onChange={(e) => setEditableMessage(e.target.value)}
            />
          </div>
          <div className="border-t py-2 px-6 flex justify-end">
            <Button label="Continuer" icon={ArrowRight} />
          </div>
        </Card>
      </Modal>
    </>
  );
}
